import React, { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faTableList } from '@fortawesome/free-solid-svg-icons';
import './PhaseRail.css';

/**
 * The phase-rail idiom, extracted from BidDetailPage (plan
 * team-bid-parity-and-fleet-registry, Build R): a sticky left aside with a pinned summary
 * row, a divider, one button per phase, and a caller-supplied footer slot.
 *
 * PRESENTATIONAL ONLY — selection is the caller's state; the right-hand figure is
 * caller-rendered (the bid passes money, the Team tab passes money-free coverage; this
 * component must never know the difference).
 *
 * The class family stays `bid-detail-rail*` DELIBERATELY: the extraction's contract is
 * byte-identical markup on the bid side (BidRailMarkup.test.tsx is the recorded baseline),
 * and one shared class family is what keeps the two surfaces looking like one system.
 */

export interface PhaseRailItem {
  key: string;
  /** Already display-ready — the bid resolves '' to 'Untitled phase' itself. */
  name: string;
  /** The right-hand figure — money on the bid, money-free coverage on the Team tab. */
  figure: ReactNode;
}

interface PhaseRailProps {
  summaryLabel: string;
  summaryFigure: ReactNode;
  dividerLabel: string;
  items: PhaseRailItem[];
  /** The summary row is active when this equals `summaryKey`. */
  activeKey: string;
  summaryKey?: string;
  onSelect: (key: string) => void;
  emptyText: string;
  /** Footer slot inside the aside — the bid's add-phase select lives here. */
  children?: ReactNode;
}

const PhaseRail: React.FC<PhaseRailProps> = ({
  summaryLabel,
  summaryFigure,
  dividerLabel,
  items,
  activeKey,
  summaryKey = 'summary',
  onSelect,
  emptyText,
  children,
}) => (
  <aside className="bid-detail-rail">
    <button
      className={`bid-detail-rail-item bid-detail-rail-summary${activeKey === summaryKey ? ' is-active' : ''}`}
      onClick={() => onSelect(summaryKey)}
    >
      <FontAwesomeIcon icon={faTableList} className="bid-detail-rail-icon" />
      <span className="bid-detail-rail-name">{summaryLabel}</span>
      <span className="bid-detail-rail-total">{summaryFigure}</span>
    </button>

    <div className="bid-detail-rail-divider">{dividerLabel}</div>

    <div className="bid-detail-rail-list">
      {items.map((item) => (
        <button
          key={item.key}
          className={`bid-detail-rail-item${activeKey === item.key ? ' is-active' : ''}`}
          onClick={() => onSelect(item.key)}
        >
          <FontAwesomeIcon icon={faLayerGroup} className="bid-detail-rail-icon" />
          <span className="bid-detail-rail-name">{item.name}</span>
          <span className="bid-detail-rail-total">{item.figure}</span>
        </button>
      ))}
      {items.length === 0 && (
        <p className="bid-detail-rail-empty">{emptyText}</p>
      )}
    </div>

    {children}
  </aside>
);

export default PhaseRail;
